import ky from "ky";
import { api } from "./NetworkBase";
import { appInsights } from "./appInisights";
import { NotificationManager } from "react-notifications";

const printHost = "http://localhost:7000";

async function GetPrinters() {
  try {
    let response = await ky.get(`${printHost}/printers/list`);
    let printers = await response.json();

    return printers;
  } catch (err) {
    // Preserve the real cause (connection refused, timeout, bad response body,
    // etc.) instead of collapsing it to a generic message - this is the only
    // signal we get for "PrintIt isn't reachable" vs "PrintIt returned garbage".
    const message = await extractErrorMessage(err, err?.message || "No response from PrintIt");
    throw new Error(message);
  }
}

async function GetPrinterMappings() {
  let printers = await GetPrinters();
  let mappings = [];

  var defaultPrinter = printers.find((x) => x.isDefault);
  if (defaultPrinter) {
    mappings.push({
      name: "Pdf",
      printerName: defaultPrinter.name,
      type: "Pdf",
      isActive: defaultPrinter.isActive,
    });
  }

  var dymoPrinter = printers.find((p) => p.name.toLowerCase().includes("dymo"));
  if (dymoPrinter)
    mappings.push({
      name: "Dymo",
      printerName: dymoPrinter.name,
      type: "Dymo",
      isActive: dymoPrinter.isActive,
    });

  var zebraPrinters = printers.filter((p) =>
    p.name.toLowerCase().includes("zdesigner")
  );
  zebraPrinters.map((printer) =>
    mappings.push({
      name: "Zebra",
      printerName: printer.name,
      type: "Zebra",
      isActive: printer.isActive,
    })
  );
  return mappings;
}

// Backend/PrintIt failures return { message } in the body; ky's HTTPError leaves
// it unread on the response, so pull it out (falling back to raw text, then the
// HTTP status) for the fullest message we can show the user. .clone() is needed
// because a response body can only be read once, and json() failing on a
// non-JSON body would otherwise leave text() nothing to read.
async function extractErrorMessage(error, fallback) {
  if (error?.response) {
    const status = error.response.status;
    const statusText = error.response.statusText || "";
    try {
      const body = await error.response.clone().json();
      const detail = body?.message || body?.error || JSON.stringify(body);
      return `HTTP ${status} ${statusText}: ${detail}`.trim();
    } catch {
      try {
        const text = await error.response.clone().text();
        if (text) return `HTTP ${status} ${statusText}: ${text.substring(0, 300)}`.trim();
      } catch {
        // response body unreadable either way - fall through to status only
      }
      return `HTTP ${status} ${statusText}`.trim();
    }
  }
  return error?.message || fallback;
}

function GetMappingForLabel(labelType, labelName, mappings, notifyOnMiss = true) {
  var mapping = mappings.filter(function (m) {
    return (
      m.type === labelType &&
      m.printerName.toLowerCase().includes(labelName.toLowerCase().trim())
    );
  })[0];

  if (!mapping) {
    mapping = mappings.filter(function (m) {
      return m.type === labelType;
    })[0];
  }

  if (!mapping) {
    if (notifyOnMiss) NotificationManager.error("No mapping for this label: " + labelType);
    return;
  }

  return mapping;
}

export async function PrintOrderNumberLabel(
  orderNumber,
  labelName,
  payType,
  selectedKit
) {
  //Get the order number label
  try {
    let orderNumberBarcodeResponse = await api
      .post("barcode", {
        json: {
          data: orderNumber.toString(),
          labelName: labelName,
          payType,
          selectedKit,
        },
      })
      .json();
    console.log("Order QR Code :  ", orderNumberBarcodeResponse);

    let printerMappings = await GetPrinterMappings();
    let orderNumberPrinterMapping = GetMappingForLabel(
      "Zebra",
      "orderBarcode",
      printerMappings
    );
    let orderNumberPrintResponse = await ky.post(`${printHost}/print/label`, {
      json: {
        Base64Xml: btoa(orderNumberBarcodeResponse),
        PrinterName: orderNumberPrinterMapping.printerName,
      },
    });
    if (orderNumberPrintResponse.status !== 200) {
      throw new Error(
        "Error printing Sample Barcode Lable.  Status code " +
          orderNumberPrintResponse.status
      );
    }
    console.log("Order Number Label Print Response", orderNumberPrintResponse);
  } catch (error) {
    appInsights.trackEvent({
      name: "Error Printing Order Number Barcode Label",
      properties: {
        message:
          "Error printing Order Number Barcode Label for order " + orderNumber,
        error: error,
      },
    });
    console.log(error);
  }
}

// New batch print function
export async function PrintOrderNumberLabelsBatch(
    orderNumbers,
    labelName,
    payType,
    selectedKit
) {
    try {
        // Get batch barcode response
        let batchBarcodeResponse = await api
            .post("barcode/batch", {
                json: {
                    orderNumbers: orderNumbers.map(num => num.toString()),
                    labelName: labelName,
                    payType,
                    selectedKit,
                },
            })
            .json();
        
        console.log("Batch QR Codes Response: ", batchBarcodeResponse);
        
        // Get printer mappings once
        let printerMappings = await GetPrinterMappings();
        let orderNumberPrinterMapping = GetMappingForLabel(
            "Zebra",
            "orderBarcode",
            printerMappings
        );
        
        // Print each label sequentially to avoid printer conflicts
        for (let i = 0; i < batchBarcodeResponse.length; i++) {
            const barcodeData = batchBarcodeResponse[i];
            
            let printResponse = await ky.post(`${printHost}/print/label`, {
                json: {
                    Base64Xml: btoa(barcodeData.barcode),
                    PrinterName: orderNumberPrinterMapping.printerName,
                },
            });
            
            if (printResponse.status !== 200) {
                throw new Error(
                    `Error printing barcode for order ${barcodeData.orderNumber}. Status code ${printResponse.status}`
                );
            }
            
            console.log(`Order ${barcodeData.orderNumber} printed successfully`);
        }
        
    } catch (error) {
        appInsights.trackEvent({
            name: "Error Printing Batch Order Number Barcode Labels",
            properties: {
                message: "Error printing batch Order Number Barcode Labels",
                orderNumbers: orderNumbers.join(', '),
                error: error.message,
            },
        });
        console.log(error);
        throw error; // Re-throw to handle in calling function
    }
}

export async function PrintBottleOrderLabels( labelName, orderNumber) {

  // printBarcode(recordId, 'sampleLabel', 'Zebra')
  try {
    let orderNumberBarcodeResponse = await api
      .post("barcode/ordered-bottles", {
        json: {
          data: orderNumber.toString(),
          // data: "9645", 
          labelName: labelName        
        },
      })
      .json();
    console.log("Bottle Order Labels :  ", orderNumberBarcodeResponse);

    let printerMappings = await GetPrinterMappings();
    let orderNumberPrinterMapping = GetMappingForLabel(
      "Zebra",
      labelName,
      printerMappings
    );
    let orderNumberPrintResponse = await ky.post(`${printHost}/print/label`, {
      json: {
        Base64Xml: btoa(orderNumberBarcodeResponse),
        PrinterName: orderNumberPrinterMapping.printerName,
      },
    });
    if (orderNumberPrintResponse.status !== 200) {
      throw new Error(
        "Error printing Sample Barcode Lable.  Status code " +
          orderNumberPrintResponse.status
      );
    }
    console.log("Order Number Label Print Response", orderNumberPrintResponse);
  } catch (error) {
    appInsights.trackEvent({
      name: "Error Printing Order Number Barcode Label",
      properties: {
        message:
          "Error printing Order Number Barcode Label for order " + orderNumber,
        error: error,
      },
    });
    console.log(error);
  }
}


export async function PrintBottleOrderLabelsByLabOrder( labelName, orderNumber) {

  // printBarcode(recordId, 'sampleLabel', 'Zebra')
  try {
    let orderNumberBarcodeResponse = await api
      .post("barcode/lab-order-number", {
        json: {
          data: orderNumber.toString(),
          // data: "9645", 
          labelName: labelName        
        },
      })
      .json();
    console.log("Bottle Order Labels :  ", orderNumberBarcodeResponse);

    let printerMappings = await GetPrinterMappings();
    let orderNumberPrinterMapping = GetMappingForLabel(
      "Zebra",
      labelName,
      printerMappings
    );
    let orderNumberPrintResponse = await ky.post(`${printHost}/print/label`, {
      json: {
        Base64Xml: btoa(orderNumberBarcodeResponse),
        PrinterName: orderNumberPrinterMapping.printerName,
      },
    });
    if (orderNumberPrintResponse.status !== 200) {
      throw new Error(
        "Error printing Sample Barcode Lable.  Status code " +
          orderNumberPrintResponse.status
      );
    }
    console.log("Order Number Label Print Response", orderNumberPrintResponse);
  } catch (error) {
    appInsights.trackEvent({
      name: "Error Printing Order Number Barcode Label",
      properties: {
        message:
          "Error printing Order Number Barcode Label for order " + orderNumber,
        error: error,
      },
    });
    console.log(error);
  }
}


// Runs the shared 4-stage print flow (label API -> print-service lookup ->
// printer mapping -> physical print) and throws a stage-tagged error so the
// Notification Manager can tell the user which part actually failed, instead
// of a generic "print failed" with no way to tell API vs printer apart.
async function PrintBottleOrderLabel(apiEndpoint, labelName, orderNumber, eventName) {
  let orderNumberBarcodeResponse;
  try {
    orderNumberBarcodeResponse = await api
      .post(apiEndpoint, {
        json: {
          data: orderNumber.toString(),
          labelName: labelName,
        },
      })
      .json();
    console.log("Bottle Order Labels :  ", orderNumberBarcodeResponse);
  } catch (error) {
    appInsights.trackEvent({
      name: eventName,
      properties: {
        stage: "Label API",
        message: `Error generating label data for order ${orderNumber}`,
        error: error,
      },
    });
    const message = await extractErrorMessage(error, error?.message || "Unknown API error");
    const fullMessage = `Label API failed (order ${orderNumber}, POST ${apiEndpoint}): ${message}`;
    NotificationManager.error(fullMessage);
    throw new Error(fullMessage);
  }

  let printerMappings;
  try {
    printerMappings = await GetPrinterMappings();
  } catch (error) {
    appInsights.trackEvent({
      name: eventName,
      properties: {
        stage: "Print Service",
        message: `Could not reach the PrintIt print service for order ${orderNumber}`,
        error: error,
      },
    });
    const fullMessage = `Print service unreachable (order ${orderNumber}): could not reach PrintIt at ${printHost} - ${error?.message || "no details"}. Make sure PrintIt is running on this PC.`;
    NotificationManager.error(fullMessage);
    throw new Error(fullMessage);
  }

  const orderNumberPrinterMapping = GetMappingForLabel(
    "Zebra",
    labelName,
    printerMappings,
    false
  );
  if (!orderNumberPrinterMapping) {
    const zebraPrinters = printerMappings
      .filter((m) => m.type === "Zebra")
      .map((m) => m.printerName);
    appInsights.trackEvent({
      name: eventName,
      properties: {
        stage: "Printer Mapping",
        message: `No Zebra printer mapped for "${labelName}", order ${orderNumber}`,
        availableZebraPrinters: zebraPrinters.join(", "),
      },
    });
    const fullMessage = `No printer mapped (order ${orderNumber}): no Zebra printer name matches "${labelName}". Zebra printers PrintIt sees: ${
      zebraPrinters.length ? zebraPrinters.join(", ") : "none"
    }. Check the printer mappings.`;
    NotificationManager.error(fullMessage);
    throw new Error(fullMessage);
  }

  try {
    const orderNumberPrintResponse = await ky.post(`${printHost}/print/label`, {
      json: {
        Base64Xml: btoa(orderNumberBarcodeResponse),
        PrinterName: orderNumberPrinterMapping.printerName,
      },
    });
    if (orderNumberPrintResponse.status !== 200) {
      throw new Error(`Status code ${orderNumberPrintResponse.status}`);
    }
    console.log("Order Number Label Print Response", orderNumberPrintResponse);
  } catch (error) {
    appInsights.trackEvent({
      name: eventName,
      properties: {
        stage: "Printer",
        message: `Printer "${orderNumberPrinterMapping.printerName}" failed for order ${orderNumber}`,
        error: error,
      },
    });
    const message = await extractErrorMessage(error, error?.message || "Unknown printer error");
    const fullMessage = `Printer failed (order ${orderNumber}): could not print "${labelName}" to "${orderNumberPrinterMapping.printerName}" - ${message}.`;
    NotificationManager.error(fullMessage);
    throw new Error(fullMessage);
  }
}

//bottle-order/big-bag
export async function GenerateBigBagLabel( labelName, orderNumber) {
  await PrintBottleOrderLabel(
    "barcode/bottle-order/big-bag",
    labelName,
    orderNumber,
    "Error Printing Big Bag Label"
  );
}

//GenerateBoxLabelPrint
export async function GenerateBoxLabelPrint( labelName, orderNumber) {
  await PrintBottleOrderLabel(
    "barcode/bottle-order/box-labels",
    labelName,
    orderNumber,
    "Error Printing Box Label"
  );
}
export async function PrintOrderedBottleLabels( labelName, orderNumber) {

  // printBarcode(recordId, 'sampleLabel', 'Zebra')
  try {
    let orderNumberBarcodeResponse = await api
      .post("barcode/ordered-bottle", {
        json: {
          data: orderNumber.toString(),
          // data: "9645",
          labelName: labelName         
        },
      })
      .json();
    console.log("Ordered Bottle Labels :  ", orderNumberBarcodeResponse);

    let printerMappings = await GetPrinterMappings();
    let orderNumberPrinterMapping = GetMappingForLabel(
      "Zebra",
      labelName,
      printerMappings
    );
    let orderNumberPrintResponse = await ky.post(`${printHost}/print/label`, {
      json: {
        Base64Xml: btoa(orderNumberBarcodeResponse),
        PrinterName: orderNumberPrinterMapping.printerName,
      },
    });
    if (orderNumberPrintResponse.status !== 200) {
      throw new Error(
        "Error printing Sample Barcode Lable.  Status code " +
          orderNumberPrintResponse.status
      );
    }
    console.log("Order Number Label Print Response", orderNumberPrintResponse);
  } catch (error) {
    appInsights.trackEvent({
      name: "Error Printing Order Number Barcode Label",
      properties: {
        message:
          "Error printing Order Number Barcode Label for order " + orderNumber,
        error: error,
      },
    });
    console.log(error);
  }
}
export const sampleInfo = {
  sampleId: "",
  sampleType: "",
  sampleLetter: function () {
    switch (this.sampleType) {
      case "Pool":
        return "Z";
      case "Coliform":
        return "B";
      case "Ecoli":
        return "E";
      default:
        return "";
    }
  },
};

/**
 *
 * @param {[sampleInfo]} sampleInfos The array of sampleInfos
 */
export async function PrintSampleLabel(sampleInfos) {
  for (let sampleInfo of sampleInfos) {
    let data = await api
      .post("barcode", {
        json: {
          substitutions: {
            "{{sampleIdentity}}": sampleInfo.sampleId,
            "{{id1}}": sampleInfo.sampleId.substring(0, 6),
            "{{id2}}": sampleInfo.sampleId.substring(6),
            "{{letter}}": sampleInfo.sampleLetter(),
          },
          labelName: "sampleBarcode",
        },
      })
      .json();

      console.log("PrintSampleLabel : Data: ",data)
    let printerMappings = await GetPrinterMappings();
    let mapping = GetMappingForLabel("Zebra", "sampleBarcode", printerMappings);

    var printRequest = {
      Base64Xml: btoa(data),
      PrinterName: mapping.printerName,
    };

    //send the print request
    try {
      let printResponse = await ky.post(`${printHost}/print/label`, {
        json: printRequest,
      });

      console.log("Sample Barcode Print Response", printResponse);

      if (printResponse.status !== 200) {
        throw new Error(
          "Error printing Sample Barcode Lable.  Status code " +
            printResponse.status
        );
      }
    } catch (err) {
      appInsights.trackEvent({
        name: "Error Printing Sample Barcode Label",
        properties: {
          message:
            "Error printing Sample Barcode Label for sample " +
            sampleInfo.sampleId,
          error: err,
        },
      });
    }
  }
}

/**
 *
 * @param {Number} orderNumber The integer number for the order
 * @param {[sampleInfo]} sampleInfos The array of sampleInfos for the order
 */
export async function PrintLabelsWithMappings(orderNumber, sampleInfos) {
  //Get printer mappings
  let mappings = await GetPrinterMappings();

  //Print the ordernumber label
  await PrintOrderNumberLabel(orderNumber, mappings);

  await PrintSampleLabel(sampleInfos, mappings);
}

export async function PrintCheckInLabel(user, dateTime) {
  let date = `${dateTime.getMonth() + 1}/${dateTime.getDate()}/${
    dateTime.getFullYear() - 2000
  }`;
  let time = `${dateTime.getHours()}:${
    (dateTime.getMinutes() < 10 ? "0" : "") + dateTime.getMinutes()
  }`;

  let data = await api
    .post("barcode", {
      json: {
        substitutions: {
          "{{user}}": user,
          "{{date}}": date,
          "{{time}}": time,
        },
        labelName: "checkinLabel",
      },
    })
    .json();
console.log("checkinLabel : Data: ",data)
  let printerMappings = await GetPrinterMappings();
  let mapping = GetMappingForLabel("Zebra", "sampleBarcode", printerMappings);

  var printRequest = {
    Base64Xml: btoa(data),
    PrinterName: mapping.printerName,
  };

  //send the print request
  let printResponse = await ky.post(`${printHost}/print/label`, {
    json: printRequest,
  });

  if (printResponse.status !== 200) {
    throw new Error(
      "Error printing Check-In Lable.  Status code " + printResponse.status
    );
  }
}

// ENB-584: prints an inventory label (product or location) on a Zebra.
// labelName is a Labels row; substitutions fill its {{placeholders}}. ZPL control characters are
// stripped from the values so typed text cannot break the label.
// Product labels go to the bottle label printer (the one bottle labels use, "sampleLabel");
// location labels stay on the sample barcode printer.
export async function PrintInventoryLabel(labelName, substitutions) {
  const clean = {};
  Object.entries(substitutions || {}).forEach(([key, value]) => {
    clean[key] = String(value ?? "").replace(/[\^~]/g, "");
  });

  let data = await api
    .post("barcode", { json: { substitutions: clean, labelName } })
    .json();

  let printerMappings = await GetPrinterMappings();
  const printerKey = labelName === "inventoryLabel" ? "sampleLabel" : "sampleBarcode";
  let mapping = GetMappingForLabel("Zebra", printerKey, printerMappings);
  if (!mapping) {
    throw new Error("No Zebra label printer found");
  }

  let printResponse = await ky.post(`${printHost}/print/label`, {
    json: { Base64Xml: btoa(unescape(encodeURIComponent(data))), PrinterName: mapping.printerName },
  });

  if (printResponse.status !== 200) {
    throw new Error("Error printing inventory label. Status code " + printResponse.status);
  }
}
